import {
  AlertTriangle,
  Download,
  Info,
  Loader2,
  Mail,
  Phone,
  RefreshCw,
  UserRoundX,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserAvatar } from "@/components/ui/user-avatar";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { useI18n } from "@/hooks/useI18n";
import { useMoneyCopy } from "@/lib/i18n/money-copy";
import { useClientDetail } from "@/hooks/useClientDetail";
import { useClientDossierExport } from "@/hooks/useClientDossierExport";
import { formatPlanAmount, formatPlanDate } from "@/lib/builder/utils/payment-format";
import { clientDisplayName } from "@/lib/dashboard/clients/client-display";
import { DetailItinerariesTable } from "@/lib/dashboard/shared/DetailItinerariesTable";
import { DetailKpiTile } from "@/lib/dashboard/shared/DetailKpiTile";
import { DetailSheetTabs } from "@/lib/dashboard/shared/DetailSheetTabs";
import {
  DetailCashflowChart,
  DetailDestinationsChart,
} from "@/lib/dashboard/shared/DetailCharts";
import {
  AgencyDashboardPeriod,
  ClientDetailKpis,
  ClientKey,
} from "@/integrations/supabase/rpc-types";
import { PlatformFeeDueBanner } from "@/lib/payments/components/PlatformFeeDueBanner";

function ClientKpiGrid({ kpis }: { kpis: ClientDetailKpis }) {
  const { t } = useI18n();
  const money = useMoneyCopy();

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
      {/*
        Ordine: quanti viaggi, quanto ha gia' pagato, quanto manca, quanto fa in tutto.
        Si legge come il conto del cliente e si chiude col totale, che e' la somma delle
        due caselle precedenti.

        Le etichette del denaro non stanno qui: le sceglie useMoneyCopy() dal ruolo di chi
        guarda, ed e' sempre il lato di chi paga. Vedi src/lib/i18n/money-copy.ts.
      */}
      {/*
        Non piu' "completati / totali": il denominatore conta solo gli itinerari con
        almeno una rata incassata, quindi le due meta' della frazione coincidono e la
        casella direbbe sempre "3 / 3". Resta il numero che conta davvero.
      */}
      <DetailKpiTile
        label={t("dashboard.overview.clients.kpiTrips")}
        value={String(kpis.itineraries_count)}
        info={t("dashboard.overview.clients.kpiTripsInfo")}
      />
      <DetailKpiTile
        label={t(`${money}.paid`)}
        value={formatPlanAmount(kpis.paid_amount)}
        info={t("dashboard.overview.clients.kpiPaidInfo")}
        tone="success"
      />
      <DetailKpiTile
        label={t(`${money}.pending`)}
        value={formatPlanAmount(kpis.remaining_amount)}
        info={t("dashboard.overview.clients.kpiRemainingInfo")}
        tone={kpis.overdue_amount > 0 ? "danger" : "neutral"}
      />
      {/*
        Il totale chiude la riga ed e' l'unico in evidenza: pagato + in attesa = totale,
        quindi e' la casella che riassume le due precedenti. Viene dai PIANI RATE, non da
        `closed_value_total`, altrimenti la somma non tornerebbe.
      */}
      <DetailKpiTile
        label={t("dashboard.overview.clients.kpiClosedValue")}
        value={formatPlanAmount(kpis.total_amount)}
        info={t("dashboard.overview.clients.kpiClosedValueInfo")}
        emphasis
      />
    </div>
  );
}

interface ClientDetailSheetProps {
  /** `null` chiude il pannello. */
  clientKey: ClientKey | null;
  onOpenChange: (open: boolean) => void;
  period: AgencyDashboardPeriod;
  periodOnly: boolean;
  onPeriodOnlyChange: (value: boolean) => void;
  /** Anagrafica gia' nota dalla riga: riempie l'intestazione mentre la scheda carica. */
  fallbackName?: string;
  fallbackEmail?: string | null;
  /**
   * Il filtro Travel Advisor della lista e' attivo. La scheda non lo applica -- e' la
   * storia della persona, non della coppia cliente/consulente -- quindi i suoi numeri
   * possono essere piu' alti di quelli della riga da cui si e' arrivati, e va detto.
   */
  advisorFilterActive?: boolean;
  /**
   * La scheda si apre sopra una Modal dell'app (es. il dettaglio di una travel
   * request). Le Modal vivono in un portale a z-index 60+, quindi pannello e velo
   * vanno alzati sopra quella soglia o finirebbero sotto la modale che li ha aperti.
   */
  aboveModal?: boolean;
}

/**
 * Scheda del singolo cliente.
 *
 * Il pannello e' largo quanto lo consente lo schermo (`sm:max-w-3xl`) e non quanto il
 * default degli sheet dell'app: dentro c'e' una tabella con le rate annidate, e alla
 * larghezza standard ogni riga andrebbe a capo.
 */
export function ClientDetailSheet({
  clientKey,
  onOpenChange,
  period,
  periodOnly,
  onPeriodOnlyChange,
  fallbackName,
  fallbackEmail,
  advisorFilterActive = false,
  aboveModal = false,
}: ClientDetailSheetProps) {
  const { t } = useI18n();
  const { data, loading, error, notFound, refresh } = useClientDetail({
    clientKey,
    period,
    periodOnly,
  });
  const { exporting, exportDossier } = useClientDossierExport();

  const client = data?.client;
  const name = client ? clientDisplayName(client) : (fallbackName ?? "");
  const email = client?.email ?? fallbackEmail ?? null;

  return (
    <Sheet open={clientKey !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`sm:max-w-3xl w-full overflow-y-auto p-0 ${aboveModal ? "z-[80]" : ""}`}
        overlayClassName={aboveModal ? "z-[80]" : undefined}
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-gray-100 space-y-3">
          <div className="flex items-start gap-3">
            <UserAvatar
              firstName={client?.first_name}
              lastName={client?.last_name}
              fullName={name}
              email={email}
              imageUrl={client?.avatar_url}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-left truncate">{name || "—"}</SheetTitle>
              <SheetDescription asChild>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm">
                  {email && (
                    <a
                      href={`mailto:${email}`}
                      className="inline-flex items-center gap-1.5 text-gray-600 hover:text-orange-600"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      {email}
                    </a>
                  )}
                  {client?.phone && (
                    <a
                      href={`tel:${client.phone}`}
                      className="inline-flex items-center gap-1.5 text-gray-600 hover:text-orange-600"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {client.phone}
                    </a>
                  )}
                  {client && !client.has_account && (
                    <Badge variant="outline" className="text-gray-500">
                      {t("dashboard.overview.clients.noAccountBadge")}
                    </Badge>
                  )}
                </div>
              </SheetDescription>
              {client?.first_seen_at && (
                <p className="text-xs text-gray-400 mt-1">
                  {t("dashboard.overview.clients.clientSince", {
                    date: formatPlanDate(client.first_seen_at),
                  })}
                </p>
              )}
            </div>
          </div>

          {/*
            Lo storico completo e' il default: aprendo una scheda anagrafica ci si aspetta
            tutta la storia del cliente, non la fetta filtrata dalla pagina. L'interruttore
            riporta la scheda dentro il periodo per chi sta confrontando con i KPI sopra.
          */}
          <div className="flex items-center gap-2">
            <Switch
              id="client-period-only"
              checked={periodOnly}
              onCheckedChange={onPeriodOnlyChange}
            />
            <Label
              htmlFor="client-period-only"
              className="text-sm font-normal text-gray-600 cursor-pointer"
            >
              {t("dashboard.overview.clients.periodOnlyToggle")}
            </Label>
            <InfoTooltip text={t("dashboard.overview.clients.periodOnlyInfo")} />

            {/*
              L'export sta sulla stessa riga dell'interruttore, non nell'intestazione
              accanto al nome: e' quell'interruttore a decidere cosa finisce nel file, e
              separarli farebbe scaricare l'ambito sbagliato a chi non se ne accorge.
              Disabilitato finche' non c'e' un payload: senza dati non c'e' niente da
              esportare, e un file vuoto e' peggio di un bottone spento.
            */}
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              disabled={!data || loading || exporting}
              onClick={() => data && exportDossier(data)}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t("dashboard.overview.clients.export.button")}
            </Button>
          </div>
        </SheetHeader>

        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 py-16 text-gray-500 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">
                {t("dashboard.overview.clients.detailLoading")}
              </span>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
                <XCircle className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  {t("dashboard.overview.errorTitle")}
                </p>
                <p className="text-sm text-gray-500">
                  {t("dashboard.overview.errorDescription")}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => refresh()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("dashboard.retry")}
              </Button>
            </div>
          )}

          {!loading && !error && notFound && (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-500">
              <UserRoundX className="h-8 w-8 text-gray-300" />
              <p className="text-sm">{t("dashboard.overview.clients.detailNotFound")}</p>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Gli avvisi stanno sopra le tab: "ho commissioni da pagare o no" e "c'e'
                  uno scaduto" sono domande a cui la scheda risponde qualunque tab sia
                  aperta. Il dettaglio per rata resta nelle righe espanse. */}
              <PlatformFeeDueBanner amount={data.stats.kpis.platform_fee_due_amount} />

              {advisorFilterActive && (
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  {t("dashboard.overview.clients.advisorFilterNotice")}
                </div>
              )}

              {data.stats.kpis.overdue_amount > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {t("dashboard.overview.clients.overdueBanner", {
                    amount: formatPlanAmount(data.stats.kpis.overdue_amount),
                  })}
                </div>
              )}

              <DetailSheetTabs
                itinerariesCount={data.itineraries.length}
                stats={
                  <>
                    <ClientKpiGrid kpis={data.stats.kpis} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <DetailCashflowChart data={data.stats.cashflow} />
                      <DetailDestinationsChart
                        data={data.stats.destinations}
                        title={t("dashboard.overview.clients.destinationsTitle")}
                        info={t("dashboard.overview.clients.destinationsInfo")}
                      />
                    </div>
                  </>
                }
                itineraries={
                  <DetailItinerariesTable
                    itineraries={data.itineraries}
                    subtitle="advisor"
                  />
                }
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ClientDetailSheet;
